import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faShield,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import API_URL from '../config';

const Settings = () => {
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      updates: true
    },
    privacy: {
      shareAnalytics: true,
      storeHistory: true
    }
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile/`);
      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
            setSettings(data.settings);
        }
      }
    } catch (e) {
      console.error("Failed to fetch settings", e);
    } finally {
      setLoading(false);
    }
  };

  const Toggle = ({ enabled, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</p>
        {description && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>
        )}
      </div>
      <button
        onClick={onChange}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
          enabled ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            enabled ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );

  const SettingsSection = ({ icon, title, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 dark:bg-gray-700 border-b border-slate-200 dark:border-slate-600">
        <div className="flex items-center">
          <FontAwesomeIcon icon={icon} className="text-slate-400 dark:text-slate-500 w-4 h-4" />
          <h3 className="ml-2 text-sm font-medium text-slate-700 dark:text-slate-200">{title}</h3>
        </div>
      </div>
      <div className="p-4 space-y-1 divide-y divide-slate-100 dark:divide-slate-700">
        {children}
      </div>
    </div>
  );

  const updateSetting = async (category, setting) => {
    // Optimistic UI update
    const previousSettings = { ...settings };
    const newSettings = {
      ...settings,
      [category]: {
        ...settings[category],
        [setting]: !settings[category][setting]
      }
    };
    setSettings(newSettings);

    try {
      const response = await fetch(`${API_URL}/api/profile/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings: newSettings }),
      });
      
      if (!response.ok) {
        // Revert on failure
        setSettings(previousSettings);
        console.error("Failed to save settings");
      }
    } catch (e) {
      setSettings(previousSettings);
      console.error("Failed to save settings", e);
    }
  };

  if (loading) {
     return (
       <div className="flex bg-gray-50 dark:bg-gray-900 h-full w-full items-center justify-center p-20">
         <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-3xl" />
       </div>
     );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="space-y-6">
        <SettingsSection icon={faBell} title="Notifications">
          <Toggle
            enabled={settings.notifications.email}
            onChange={() => updateSetting('notifications', 'email')}
            label="Email Notifications"
            description="Receive email updates about your analyses"
          />
          <Toggle
            enabled={settings.notifications.updates}
            onChange={() => updateSetting('notifications', 'updates')}
            label="Product Updates"
            description="Receive updates about new features and improvements"
          />
        </SettingsSection>

        <SettingsSection icon={faShield} title="Privacy">
          <Toggle
            enabled={settings.privacy.shareAnalytics}
            onChange={() => updateSetting('privacy', 'shareAnalytics')}
            label="Share Analytics"
            description="Help improve the platform by sharing anonymous usage data"
          />
          <Toggle
            enabled={settings.privacy.storeHistory}
            onChange={() => updateSetting('privacy', 'storeHistory')}
            label="Store History"
            description="Save your analysis history for future reference"
          />
        </SettingsSection>
      </div>
    </div>
  );
};

export default Settings;